import { useToast } from "@/context/ToastContext";
import { ApiError } from "@/types/apiTypes";
import { useState } from "react";
import { AxiosError } from "axios";
// Import the ToastType
type ToastType = "success" | "error" | "warning" | "info";

/**
 * Custom hook for handling errors and displaying them as toasts
 */
export const useToastHandler = () => {
  const { showToast, dismiss } = useToast();

  const [lastErrorBody, setLastErrorBody] = useState("");
  const [lastErrorTimestamp, setLastErrorTimestamp] = useState(0);

  return {
    /**
     * Handle API errors by extracting the error message and displaying it
     * @param error The error object to handle
     */
    handleError: (error: unknown, duration?: number, devError?: string) => {
      // if not production, log the error
      if (import.meta.env.DEV) {
        console.log(">>>error", error);
      }

      let errorBody = "An unexpected error occurred";

      if (
        error instanceof AxiosError &&
        error.response?.status &&
        error.response.data.error
      ) {
        errorBody = error.response.data.error;
      } else if (error instanceof ApiError && error.data?.error) {
        errorBody = error.data.error;
      } else if (error instanceof Error) {
        errorBody = error.message;
      } else {
        // if is string, show the string
        if (typeof error === "string") {
          errorBody = error;
        }
      }

      // Do not show the same error multiple times within 3 seconds
      if (
        lastErrorBody === errorBody &&
        Date.now() - lastErrorTimestamp < 3000
      ) {
        return;
      }

      // Process new error
      setLastErrorBody(errorBody);
      setLastErrorTimestamp(Date.now());

      // show the raw error in a modal for dev mode:
      if (devError && import.meta.env.MODE !== "production") {
        errorBody = "DEBUG: " + devError;
      }

      showToast(errorBody, "error", duration);
    },

    /**
     * Display a success message
     * @param message The success message to display
     * @param duration Optional duration in milliseconds before auto dismiss
     */
    handleSuccess: (message: string, duration?: number) => {
      showToast(message, "success", duration);
    },

    /**
     * Display a warning message
     * @param message The warning message to display
     * @param duration Optional duration in milliseconds before auto dismiss
     */
    handleWarning: (message: string, duration?: number) => {
      showToast(message, "warning", duration);
    },

    /**
     * Display an info message
     * @param message The info message to display
     * @param duration Optional duration in milliseconds before auto dismiss
     */
    handleInfo: (message: string, duration?: number) => {
      showToast(message, "info", duration);
    },

    /**
     * Clear any displayed toasts
     */
    clearToasts: () => {
      dismiss();
    },

    showToast: (
      message: string,
      type: ToastType = "info",
      duration?: number
    ) => {
      showToast(message, type, duration);
    },

    dismiss,
  };
};
